import React from 'react';
import { cn } from '@/lib/utils';
import { TIER_CARDS, normalizeTierId, type TierId } from './tierCatalog';

interface TierPricingCardsProps {
  /** Raw tier id from the backend (e.g. "free", "starter", "pro"). */
  currentTier: string;
  /** Tier currently mid-upgrade (shows the spinner on its button). */
  upgradingTier: string | null;
  /**
   * Fired for EVERY card click — current, upgrade, downgrade or Enterprise.
   * The parent decides what happens (checkout, confirm dialog, explainer):
   * per the product spec, every tier shows an ACTIVE button that either
   * takes you to the change flow or tells you plainly what you can do.
   */
  onSelect: (tierId: TierId) => void;
}

const Spinner: React.FC = () => (
  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24" aria-hidden="true">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

/**
 * The four tier offer cards (pricing v3): formula pricing, a worked example,
 * "Most popular" anchor on Pro, and the current workspace tier marked.
 */
const TierPricingCards: React.FC<TierPricingCardsProps> = ({
  currentTier,
  upgradingTier,
  onSelect,
}) => {
  const current = normalizeTierId(currentTier);
  const currentIndex = TIER_CARDS.findIndex((t) => t.id === current);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
      {TIER_CARDS.map((tier, index) => {
        const isCurrent = tier.id === current;
        const isDowngrade = index < currentIndex;
        const isUpgrading = upgradingTier === tier.id;

        const label = isCurrent
          ? 'Your current plan'
          : isDowngrade
            ? tier.id === 'free'
              ? 'Move to Free'
              : `Downgrade to ${tier.name}`
            : tier.cta;

        return (
          <div
            key={tier.id}
            data-testid={`tier-card-${tier.id}`}
            className={cn(
              'relative flex flex-col rounded-2xl border p-5 pt-6 bg-surface-elevated',
              'transition-[border-color,box-shadow] duration-fast',
              tier.popular
                ? 'border-2 border-primary-500 dark:border-primary-350 shadow-lg shadow-primary-500/10'
                : 'border-border-primary',
              isCurrent && 'bg-primary-50 dark:bg-primary-900/20'
            )}
          >
            {tier.popular && (
              <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-primary-500 dark:bg-primary-350 px-3 py-0.5 text-[11px] font-bold uppercase tracking-wider ">
                Most popular
              </span>
            )}
            {isCurrent && (
              <span className="absolute top-4 right-4 rounded-full bg-primary-500 dark:bg-primary-350 px-2 py-0.5 text-xs font-medium ">
                Current
              </span>
            )}

            <h4 className="text-lg font-bold text-text-primary">{tier.name}</h4>
            <p className="mt-0.5 mb-4 min-h-[38px] text-[13px] leading-snug text-text-tertiary">
              {tier.tagline}
            </p>

            <p className="text-2xl font-bold text-text-primary">
              {tier.baseMonthly === null ? (
                'Custom'
              ) : tier.baseMonthly === 0 ? (
                '€0'
              ) : (
                <>
                  €{tier.baseMonthly}
                  <span className="text-sm font-semibold text-text-secondary">
                    {' '}
                    + €{tier.perSeatMonthly} per seat / mo
                  </span>
                </>
              )}
            </p>
            {/* Both lines are optional: a plan we quote per deal states no
                price here, and the empty wrapper keeps the gap above the
                button identical across the row. */}
            <div className="mb-5">
              {tier.priceNote && (
                <p className="mt-1 tabular-nums text-xs text-text-secondary">{tier.priceNote}</p>
              )}
              {tier.example && (
                <p className="mt-0.5 text-xs text-text-tertiary">{tier.example}</p>
              )}
            </div>

            {/* Every card gets an active button — clicking always answers
                "what happens if I pick this?" (spec: no dead ends). */}
            <button
              type="button"
              onClick={() => onSelect(tier.id)}
              disabled={isUpgrading}
              className={cn(
                'mt-auto inline-flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-bold',
                'focus-ring transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                isCurrent || isDowngrade
                  ? 'border-[1.5px] border-border-secondary text-text-secondary hover:bg-text-primary/5'
                  : tier.popular
                    ? 'bg-primary-500 dark:bg-primary-350 text-primary-ink hover:bg-primary-600 dark:hover:bg-primary-400'
                    : 'border-[1.5px] border-primary-500 dark:border-primary-350 text-primary-600 dark:text-primary-350 hover:bg-primary-500/10'
              )}
            >
              {isUpgrading && <Spinner />}
              {isUpgrading ? 'Opening checkout…' : label}
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default TierPricingCards;
